import { setFieldValue } from 'actions/ui/ui';
import { PaymentInformation } from 'components/PaymentInformation/PaymentInformation';
import { Spinner } from 'components/Spinner/Spinner';
import { StepList } from 'components/StepList/StepList';
import { Wizard } from 'components/Wizard/Wizard';
import { FF_NEW_INDIAN_WIZARD } from 'constants/features';
import { PAYMENT_INFO_STEP_COMPLETED } from 'constants/fields';
import { STEP_PAY } from 'constants/paymentSteps';
import { useExperimentFlag } from 'hooks/useExperiments/useExperimentFlag';
import { usePreserveRecipient } from 'hooks/usePreserveRecipient/usePreserveRecipient';
import React, { Suspense } from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from 'reducers/types';
import { getSenderCountry } from 'selectors/entities/order/order';
import { getRecipientId } from 'selectors/entities/recipient';
import { getFieldValue, getPaymentSteps } from 'selectors/ui/ui';
import { useDispatch } from 'store/configureStore';

const CURRENT_STEP = 0;

const WizardIndia = React.lazy(() =>
  import(
    /* webpackChunkName: "WizardIndia" */ 'components/india/Wizard/WizardIndia'
  ).then(module => ({
    default: module.WizardIndia,
  })),
);

const PaymentEntryPoint = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const country = useSelector(getSenderCountry);
  const recipientId = useSelector(getRecipientId);
  const steps = useSelector(getPaymentSteps);
  const paymentInformationCompleted = useSelector((state: RootState) =>
    getFieldValue(state, PAYMENT_INFO_STEP_COMPLETED),
  );
  const { value: showIndianWizard } = useExperimentFlag({
    flagName: FF_NEW_INDIAN_WIZARD,
    userContext: {
      countryFrom: country,
      portalCode: recipientId,
    },
  });
  const { createUrlWithRecipient } = usePreserveRecipient();

  const handleNextStep = () => {
    dispatch(setFieldValue(PAYMENT_INFO_STEP_COMPLETED, true));
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: createUrlWithRecipient, navigate
  useEffect(() => {
    if (!paymentInformationCompleted) {
      const url = createUrlWithRecipient(
        `/${STEP_PAY}/payment`,
        window.location.search,
      );

      navigate(url, { replace: true });
    }
  }, [paymentInformationCompleted]);

  if (paymentInformationCompleted) {
    return (
      <Suspense fallback={<Spinner className="h-[50vh]" />}>
        {showIndianWizard ? <WizardIndia /> : <Wizard />}
      </Suspense>
    );
  }

  return (
    <>
      <StepList activeStepIndex={CURRENT_STEP} totalSteps={steps.length + 1} />
      <PaymentInformation onNext={handleNextStep} />
    </>
  );
};

export { PaymentEntryPoint };
